import { useTranslation } from "react-i18next";
import { Building2, XIcon } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import { AddCompanyForm } from "@/components/add-company-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// eslint-disable-next-line
export enum ENUM_ADD_COMPANY_QUERY_PARAM {
  name = "action",
  value = "add-company",
}

export const AddCompanyDialog = () => {
  const { t } = useTranslation(["common"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const isOpen =
    searchParams.get(ENUM_ADD_COMPANY_QUERY_PARAM.name) ===
    ENUM_ADD_COMPANY_QUERY_PARAM.value;

  const closeDialog = () =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(ENUM_ADD_COMPANY_QUERY_PARAM.name);
      return next;
    });

  return (
    // `onOpenChange` is what wires up Esc and overlay-click. Without it the
    // dialog could only be dismissed by its own close button.
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent
        // The built-in close is suppressed so the header owns a single, properly
        // sized one — rendering both stacked two X's in the same corner.
        showCloseButton={false}
        className="gap-0 overflow-hidden p-0 shadow-2xl sm:max-w-xl"
      >
        <DialogHeader className="relative gap-0 border-b border-border p-5 pe-14 text-start">
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className="bg-gradient-brand flex size-10 shrink-0 items-center justify-center rounded-xs text-white"
            >
              <Building2 size={19} />
            </span>
            <div className="min-w-0 space-y-1">
              <DialogTitle className="text-base leading-snug">
                {t("common:chrome.add_new_company")}
              </DialogTitle>
              <DialogDescription className="text-xs leading-relaxed">
                {t(
                  "common:chrome.add_a_new_company_to_manage_with_mai_hrms_youll_be_switc",
                )}
              </DialogDescription>
            </div>
          </div>

          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            onClick={closeDialog}
            aria-label={t("common:buttons.close")}
            data-tooltip-id="hrm-tooltip"
            data-tooltip-content={t("common:buttons.close")}
            className="absolute top-4 end-4 rounded-full text-muted-foreground transition-all hover:bg-muted hover:text-foreground active:scale-90"
          >
            <XIcon size={15} aria-hidden />
          </Button>
        </DialogHeader>

        {/* Caps the dialog below the viewport on short screens — the default
            DialogContent has no height bound, so a 5-field form could clip. */}
        <div className="scrollbar-thin max-h-[65svh] overflow-y-auto p-5">
          <AddCompanyForm
            columns={2}
            onSuccess={closeDialog}
            onCancel={closeDialog}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
